import React, { useEffect, useState } from "react";

import styles from "~/pages/CharacterSheet/widgets/RollNotification/Notification.module.scss";
import { type Notification, TagActionType } from "~/pages/CharacterSheet/types";
import { Badge } from "~/widgets/Badge/Badge";

export interface NotificationProps {
  notification: Notification;
  onDismiss: (id: string) => void;
  duration?: number;
}

/**
 * Notification component for displaying temporary toast notifications
 * in the top-right corner when a roll is made or a tag action is triggered.
 */
const NotificationComponent: React.FC<NotificationProps> = ({
  notification,
  onDismiss,
  duration = 4000,
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const id = notification.data.id;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onDismiss(id), 300); // Match animation duration
    }, duration);

    return () => clearTimeout(timer);
  }, [id, onDismiss, duration]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(id), 300);
  };

  if (notification.kind === "roll") {
    const roll = notification.data;
    return (
      <div
        className={`
                    ${styles.notification}
                    ${roll.isCritFumble ? styles.critFumble : ""}
                    ${roll.isFumble ? styles.fumble : ""}
                    ${isExiting ? styles.exiting : ""}
                `}
      >
        <div className={styles.content}>
          <div className={styles.header}>
            <span className={styles.skillName}>{roll.name}</span>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={handleDismiss}
              aria-label="Dismiss notification"
            >
              &times;
            </button>
          </div>
          <div className={styles.result}>
            <span className={styles.hits}>{roll.hits}</span>
            <span className={styles.hitsLabel}>
              {roll.hits === 1 ? "Hit" : "Hits"}
            </span>
            {roll.flatHits !== undefined && roll.flatHits !== 0 && (
              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--colorSuccess)', marginTop: '0.1rem' }}>
                ({roll.hits - roll.flatHits} + {roll.flatHits} hit{Math.abs(roll.flatHits) === 1 ? "" : "s"})
              </div>
            )}
          </div>
          <div className={styles.diceList}>
            {roll.rolls.map((val, idx) => (
              <span
                key={idx}
                className={`
                                    ${styles.die}
                                    ${val >= 5 ? styles.hitDie : ""}
                                    ${val === 1 ? styles.oneDie : ""}
                                `}
              >
                {val}
              </span>
            ))}
          </div>
          {roll.isCritFumble && (
            <div className={styles.status}>CRIT FUMBLE!</div>
          )}
          {roll.isFumble && !roll.isCritFumble && (
            <div className={styles.status}>Fumble</div>
          )}
        </div>
      </div>
    );
  }

  // Rest Notification
  if (notification.kind === "rest") {
    const rest = notification.data;
    const restLabels = {
      snack: "Snack Break",
      meal: "Meal Break",
      fullRest: "Good Night's Rest",
    };

    return (
      <div
        className={`${styles.notification} ${styles.positive} ${isExiting ? styles.exiting : ""}`}
      >
        <div className={styles.content}>
          <div className={styles.header}>
            <span className={styles.skillName}>{restLabels[rest.restType]}</span>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={handleDismiss}
              aria-label="Dismiss notification"
            >
              &times;
            </button>
          </div>
          <div className={styles.restEffects}>
            {rest.resilienceGained > 0 && (
              <div className={styles.restEffect}>
                +{rest.resilienceGained} Resilience
              </div>
            )}
            {rest.strainRemoved > 0 && (
              <div className={styles.restEffect}>
                -{rest.strainRemoved} Strain
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Tag Action Notification
  const tag = notification.data;
  const isNegative =
    tag.type === TagActionType.LOSE_HEALTH ||
    tag.type === TagActionType.LOSE_RESILIENCE ||
    tag.type === TagActionType.ACQUIRE_STRAIN;

  const getLabel = (type: TagActionType) => {
    switch (type) {
      case TagActionType.GAIN_HEALTH:
      case TagActionType.LOSE_HEALTH:
        return "Health";
      case TagActionType.LOSE_RESILIENCE:
        return "Resilience";
      case TagActionType.ACQUIRE_STRAIN:
        return "Strain Acquired";
      case TagActionType.REDUCE_STRAIN:
        return "Strain Removed";
    }
  };
  const label = getLabel(tag.type);

  return (
    <div
      className={`
                ${styles.notification}
                ${isNegative ? styles.negative : styles.positive}
                ${isExiting ? styles.exiting : ""}
            `}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.titleWrapper}>
            {tag.sourceType === "Item" && (
              <Badge variant="item" className={styles.notificationBadge}>Item</Badge>
            )}
            {tag.sourceType === "Ability" && (
              <Badge variant="ability" className={styles.notificationBadge}>Ability</Badge>
            )}
            <span className={styles.skillName}>{tag.sourceName || label}</span>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleDismiss}
            aria-label="Dismiss notification"
          >
            &times;
          </button>
        </div>
        <div className={styles.result}>
          <span className={styles.hits}>
            {tag.value !== undefined
              ? (
                tag.type === TagActionType.REDUCE_STRAIN
                  ? tag.value
                  : (tag.type === TagActionType.ACQUIRE_STRAIN || !isNegative ? `+${tag.value}` : `-${tag.value}`)
              )
              : ""}
          </span>
          {tag.sourceName && (
            <span className={styles.hitsLabel}>
              {label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationComponent;
